import { defineElement, FC, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { Button } from '@atoms/button/button';
import { areaProjectGetById } from '@bpd-library/utilities';
import { openModal } from '@source/store/modals';
import { updateSmartSubscriptionProject } from '@source/store/smart-subscription';
import { useTranslate } from '@source/utilities/hooks';
import {
    isStoreAddressDataComplete,
    subscribeToProjectPhase,
    subscriptionStore,
} from '@store/subscription/store';

import { SUBSCRIPTION_MODAL_ID } from '../smart-subscription';

const SubscriptionBox: FC = ({ element }) => {
    const [boxTitle] = useProp<string | Text | null>('boxTitle', null);
    const [boxContent] = useProp<string | Text>('boxContent');
    const [variant] = useProp<string | null>('variant', null);
    const [buttonLabel] = useProp<string | Text>('buttonLabel');
    const [projectPhaseId] = useProp<string>('projectPhaseId');
    const [projectPhaseName] = useProp<string>('projectPhaseName');
    const [districtId] = useProp<string>('districtId');
    const [constructionNumberName] = useProp<string>('constructionNumberName', '');
    const [deleteWhenSubscribed] = useProp<boolean>('deleteWhenSubscribed', false);

    const areaId = window.__ENVIRONMENT_DETAILS__.areaId;

    let className = 'c-subscription-box';
    className = variant ? `${className} subscription-box--${variant}` : className;

    subscriptionStore.subscribe(
        ({ projectPhases }) => {
            if (deleteWhenSubscribed) {
                const projectPhaseIsSubscribed = !!projectPhases?.find(
                    (id) => id === projectPhaseId,
                );

                if (projectPhaseIsSubscribed) element.remove();
            }
        },
        ['projectPhases'],
    );

    async function handleSubscription() {
        const { user, address } = subscriptionStore.getState();
        const areaProject = await areaProjectGetById(areaId);

        updateSmartSubscriptionProject({
            districtId,
            projectPhaseId,
            title: constructionNumberName,
            area: areaProject.name || '',
            name: projectPhaseName,
        });

        if (user && address && isStoreAddressDataComplete(address)) {
            subscribeToProjectPhase({
                areaInfo: {
                    projectPhaseId,
                    areaProjectId: areaProject.id,
                    areaProjectName: areaProject.name || '',
                    districtId,
                },
                userInfo: {
                    firstName: user.firstName || '',
                    lastName: user.lastName,
                    initials: user.initials,
                    email: user.email,
                    gender: user.gender,
                },
            }).then(() => {
                openModal(SUBSCRIPTION_MODAL_ID);
            });
        } else {
            openModal(SUBSCRIPTION_MODAL_ID);
        }
    }

    return (
        <div class={className}>
            <div>
                <h4 class="subscription-box__title">
                    {boxTitle
                        ? boxTitle
                        : useTranslate('SubscriptionBox.Title', {
                              replace: [{ id: 'Phase', value: projectPhaseName }],
                          })}
                </h4>

                <p class="subscription-box__description">{boxContent}</p>
            </div>

            <Button
                variant={variant === 'vertical' ? 'tertiary' : 'primary'}
                width={variant === 'vertical' ? 'full-width' : undefined}
                type="button"
                label={buttonLabel}
                classes="subscription-box__button"
                onClick={() => handleSubscription()}
            />
        </div>
    );
};

SubscriptionBox.props = {
    boxTitle: {
        type: String,
    },
    boxContent: {
        type: String,
        required: true,
    },
    variant: {
        type: String,
    },
    buttonLabel: {
        type: String,
        required: true,
    },
    projectPhaseId: {
        type: String,
        required: true,
    },
    projectPhaseName: {
        type: String,
        required: true,
    },
    districtId: {
        type: String,
        required: true,
    },
    propertyTypeId: {
        type: String,
    },
    constructionNumberName: {
        type: String,
    },
    deleteWhenSubscribed: {
        type: Boolean,
    },
};

defineElement('subscription-box', SubscriptionBox);
